import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../../components/ui/Modal';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const createLocalKey = () => (
  globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`
);

const createStage = (assignedUserId = '') => ({
  localKey: createLocalKey(),
  name: '',
  assignedUserId,
});

const getRole = (member) => (
  member?.roles?.some((role) => String(role).toLowerCase() === 'admin') ? 'Admin' : 'Staff'
);

const WorkflowEditorModal = ({ isOpen, onClose, onSubmit, workflow, members, isSubmitting }) => {
  const activeMembers = useMemo(
    () => members.filter((member) => member?.isEnabled),
    [members]
  );
  const assigneeOptions = useMemo(
    () => activeMembers.map((member) => ({
      value: member.id,
      label: `${member.firstName} ${member.lastName} — ${getRole(member)}`,
    })),
    [activeMembers]
  );
  const defaultAssigneeId = activeMembers[0]?.id || '';
  const [name, setName] = useState('');
  const [stages, setStages] = useState([createStage()]);
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (!isOpen) return;

    if (workflow) {
      setName(workflow.name || '');
      setStages(
        [...(workflow.stages || [])]
          .sort((first, second) => first.order - second.order)
          .map((stage) => ({
            localKey: stage.id || createLocalKey(),
            name: stage.name || '',
            assignedUserId: stage.assignedUserId || '',
          }))
      );
    } else {
      setName('');
      setStages([createStage(defaultAssigneeId)]);
    }
    setValidationError('');
  }, [defaultAssigneeId, isOpen, workflow]);

  const updateStage = (localKey, field, value) => {
    setStages((current) => current.map((stage) => (
      stage.localKey === localKey ? { ...stage, [field]: value } : stage
    )));
  };

  const addStage = () => {
    setStages((current) => [...current, createStage(defaultAssigneeId)]);
  };

  const removeStage = (localKey) => {
    setStages((current) => current.filter((stage) => stage.localKey !== localKey));
  };

  const moveStage = (index, direction) => {
    const destination = index + direction;
    if (destination < 0 || destination >= stages.length) return;

    setStages((current) => {
      const reordered = [...current];
      [reordered[index], reordered[destination]] = [reordered[destination], reordered[index]];
      return reordered;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setValidationError('');

    if (!name.trim()) {
      setValidationError('Enter a workflow name.');
      return;
    }

    if (stages.length === 0) {
      setValidationError('Add at least one workflow stage.');
      return;
    }

    if (stages.some((stage) => !stage.name.trim() || !stage.assignedUserId)) {
      setValidationError('Every stage needs a name and an assignee.');
      return;
    }

    await onSubmit({
      name: name.trim(),
      stages: stages.map((stage, index) => ({
        name: stage.name.trim(),
        order: index + 1,
        assignedUserId: stage.assignedUserId,
      })),
    });
  };

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={workflow ? 'Edit Workflow' : 'Create Workflow'}
      maxWidth="max-w-3xl"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {validationError && (
          <div className="flex items-start gap-3 rounded-xl border border-error/20 bg-error/10 p-4">
            <Icon name="AlertCircle" size={19} color="var(--color-error)" className="mt-0.5 flex-shrink-0" />
            <p className="text-sm text-error font-caption">{validationError}</p>
          </div>
        )}

        <Input
          label="Workflow name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="For example: Custom order approval"
          required
          disabled={isSubmitting}
        />

        <div>
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <h3 className="font-heading font-semibold text-foreground">Stages</h3>
              <p className="mt-1 text-sm text-muted-foreground font-caption">
                Requests move through these stages from top to bottom.
              </p>
            </div>
            <Button type="button" variant="outline" size="sm" iconName="Plus" onClick={addStage} disabled={isSubmitting}>
              Add Stage
            </Button>
          </div>

          {activeMembers.length === 0 && (
            <div className="mb-4 rounded-xl border border-warning/20 bg-warning/10 p-4 text-sm text-warning font-caption">
              Add or enable a team member before configuring workflow assignees.
            </div>
          )}

          <div className="space-y-3">
            {stages.map((stage, index) => (
              <div key={stage.localKey} className="rounded-xl border border-border bg-muted/20 p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm text-primary font-caption font-semibold">
                      {index + 1}
                    </span>
                    <span className="text-sm text-muted-foreground font-caption">Stage {index + 1}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => moveStage(index, -1)}
                      disabled={isSubmitting || index === 0}
                      aria-label={`Move stage ${index + 1} up`}
                    >
                      <Icon name="ArrowUp" size={16} />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => moveStage(index, 1)}
                      disabled={isSubmitting || index === stages.length - 1}
                      aria-label={`Move stage ${index + 1} down`}
                    >
                      <Icon name="ArrowDown" size={16} />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-error hover:bg-error/10 hover:text-error"
                      onClick={() => removeStage(stage.localKey)}
                      disabled={isSubmitting || stages.length === 1}
                      aria-label={`Remove stage ${index + 1}`}
                    >
                      <Icon name="Trash2" size={16} />
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Input
                    label="Stage name"
                    value={stage.name}
                    onChange={(event) => updateStage(stage.localKey, 'name', event.target.value)}
                    placeholder="Enter stage name"
                    required
                    disabled={isSubmitting}
                  />
                  <Select
                    label="Assigned team member"
                    value={stage.assignedUserId}
                    options={assigneeOptions}
                    onChange={(value) => updateStage(stage.localKey, 'assignedUserId', value)}
                    placeholder="Select an assignee"
                    required
                    disabled={isSubmitting || activeMembers.length === 0}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-border pt-5 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            loading={isSubmitting}
            disabled={activeMembers.length === 0}
            iconName={workflow ? 'Save' : 'Plus'}
          >
            {workflow ? 'Save Changes' : 'Create Workflow'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default WorkflowEditorModal;
